import { arrayOf, bool, func, shape, string } from 'prop-types';
import React from 'react';
import dynamic from 'next/dynamic';
import SpreadBundle from 'components/SpreadBundle';
import ItemBundle from 'components/ItemBundle';
import ContentBundle from 'components/ContentBundle';
import TriggerOnMount from 'components/TriggerOnMount';
import Loading from 'components/Loading';
import LazyLoad from 'components/LazyLoad';

const PromotionalRibbon = dynamic(
  () => import('components/PromotionalRibbon'),
  {
    ssr: false
  }
);

/**
 * CollectionLayout is a component that handles rendering a list of bundles
 * passed in via the contentBundles prop. All bundles get wrapped in a
 * div.CollectionLayout.
 */

const LAZY_LOAD_OFFSET = 400; // px

const BUNDLE_MAP = {
  spread: SpreadBundle,
  item: ItemBundle
};

const BANNER_PATHS = [
  '/collection/home-for-the-holidays',
  '/collection/plant-design'
];

// This is a TEMP workaround for full bleed banners. We should update workflow so
// that we can set a layout of 'banner' instead of interrupter to toggle this behavior.
function addCustomProps(path, idx, bundle) {
  if (BANNER_PATHS.includes(path) && idx === 0) {
    return {
      ...bundle,
      layout: 'banner'
    };
  }
  return bundle;
}

function CollectionLayout(props) {
  const {
    contentBundles,
    onLoadMore,
    isLoadingMore,
    hasMore,
    pathWithoutParams
  } = props;

  return (
    <div className="CollectionLayout">
      {pathWithoutParams === '/' && <PromotionalRibbon />}
      {contentBundles.map((bundleProps, idx) => (
        <ContentBundle
          key={bundleProps.id}
          idx={idx}
          components={BUNDLE_MAP}
          {...addCustomProps(pathWithoutParams, idx, bundleProps)}
        />
      ))}
      {hasMore && (
        <div className="CollectionLayout__show-more">
          {isLoadingMore === true && (
            <div className="CollectionLayout__loading">
              <Loading />
            </div>
          )}

          {isLoadingMore === false && (
            <LazyLoad height={400} offset={LAZY_LOAD_OFFSET} once>
              <TriggerOnMount fn={onLoadMore} />
            </LazyLoad>
          )}
        </div>
      )}
    </div>
  );
}

CollectionLayout.defaultProps = {
  contentBundles: undefined,
  pathWithoutParams: undefined
};

CollectionLayout.propTypes = {
  contentBundles: arrayOf(
    shape({
      // we only care about id here, but a bundle needs other props like
      // layout, data, and items to be truly useful.
      id: string.isRequired
    })
  ),
  hasMore: bool.isRequired,
  onLoadMore: func.isRequired,
  isLoadingMore: bool.isRequired,
  pathWithoutParams: string
};

export default CollectionLayout;
